import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Button, buttonVariants } from '@/components/ui/button';
import { RefreshCw, FileText } from 'lucide-react';
import { useProject } from '@/context/ProjectContext';
import { useEVM } from '@/context/EVMContext';
import { useTargetCost } from '@/context/TargetCostContext';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ExportButtons } from '@/components/Export/ExportButtons';
import { createTranscript } from '@/utils/transcript';
import type { ChatAnalysis } from '@/types/whatsapp';

interface ToolActionsProps {
  toolType: string;
}

export function ToolActions({ toolType }: ToolActionsProps) {
  const { state: projectState, dispatch: projectDispatch } = useProject();
  const evmContext = toolType === 'evm' ? useEVM() : null;
  const targetCostContext = toolType === 'target-cost' ? useTargetCost() : null;

  const handleClear = () => {
    if (toolType === 'evm' && evmContext) {
      evmContext.dispatch({ type: 'CLEAR_DATA' });
    } else if (toolType === 'target-cost' && targetCostContext) {
      targetCostContext.dispatch({ type: 'CLEAR_DATA' });
    } else {
      projectDispatch({ type: 'CLEAR_DATA' });
    }
  };

  const tooltipContent = 
    toolType === 'evm' ? 'Clear EVM data' :
    toolType === 'target-cost' ? 'Clear target cost data' :
    toolType === 'cvr' ? 'Clear project data' :
    'Clear form data';

  return (
    <div className="flex items-center gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              onClick={handleClear}
              size="sm"
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Clear Form
            </Button>
          </TooltipTrigger>
          <TooltipContent>{tooltipContent}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {toolType === 'cvr' && <ExportButtons data={projectState.data} />}
    </div>
  );
}

export function TranscriptButton() {
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState<ChatAnalysis | null>(null);

  const handleCreateTranscript = async (analysis: ChatAnalysis) => {
    try {
      if (!analysis) {
        throw new Error('No analysis data available');
      }
      await createTranscript(analysis);
      toast({
        title: 'Success',
        description: 'Transcript created successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create transcript',
        variant: 'destructive',
      });
    }
  };

  // Get analysis data from URL state
  useEffect(() => {
    const state = location.state as { analysis?: ChatAnalysis };
    if (state?.analysis) {
      setAnalysis(state.analysis);
    }
  }, [location]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCreateTranscript(analysis)}
            disabled={!analysis}
            className="gap-2"
          >
            <FileText className="h-4 w-4" />
            Create Transcript
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {analysis ? 'Generate Word-compatible transcript' : 'Analyze chat first to create transcript'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}